import React, { useState, useMemo } from 'react'
import settings from 'popups/Settings'
import styles from './style.module.scss'
import { Dropdown } from 'antd'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import moment from 'moment'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { localizedTimeString } from 'pages/calendar/utils/DateUtils'
import { initialState } from 'redux/settings/types'
import type { WorkHour } from 'redux/settings/types'

interface TimeOption {
  value: number
  label: string
}

interface WorkHoursPanelProps {
  onWorkHoursChange: (workHours: WorkHour[]) => void
}

const WorkHoursPanel: React.FC<WorkHoursPanelProps> = ({ onWorkHoursChange }) => {
  const { t } = useTranslation()

  const calSettings = settings.getValue('calendar')
  const [workHours, setWorkHours] = useState<WorkHour[]>(
    calSettings.workHours || initialState.calendar.workHours,
  )

  const dayNames = useMemo(() => moment.localeData().weekdaysShort(), [])
  console.log('dayNames : ', dayNames)

  const formatTime = (minutes: number): string => {
    const date = new Date(0)
    date.setHours(0, minutes, 0, 0)
    return localizedTimeString(date)
  }

  // 시간 옵션 생성 (30분 간격)
  const generateTimeOptions = (): TimeOption[] => {
    const options: TimeOption[] = []
    for (let hour = 0; hour < 24 * 2; hour++) {
      const totalMinutes = hour * 30
      options.push({
        value: totalMinutes,
        label: formatTime(totalMinutes),
      })
    }
    return options
  }

  const timeOptions = useMemo(() => generateTimeOptions(), [])

  const createTimeDropdownItems = (index: number, field: 'start' | 'end') => {
    return timeOptions.map((option) => ({
      key: option.value,
      label: option.label,
      onClick: () => handleTimeChange(index, field, option.value),
    }))
  }

  const handleDayToggle = (index: number) => {
    const updatedWorkHours = [...workHours]
    updatedWorkHours[index].enabled = !updatedWorkHours[index].enabled
    setWorkHours(updatedWorkHours)

    onWorkHoursChange(updatedWorkHours)
  }

  const handleTimeChange = (index: number, field: 'start' | 'end', value: number) => {
    const updatedWorkHours = [...workHours]
    updatedWorkHours[index][field] = value
    setWorkHours(updatedWorkHours)

    onWorkHoursChange(updatedWorkHours)
  }

  const getSelectedKey = (index: number, field: 'start' | 'end'): number | null => {
    return workHours[index]?.[field] || null
  }

  const handleDropdownOpen = (index: number, field: 'start' | 'end', isOpen: boolean) => {
    if (isOpen) {
      const selectedValue = getSelectedKey(index, field)
      if (selectedValue != null) {
        setTimeout(() => {
          // 모든 드롭다운 메뉴를 찾아서 해당 인덱스와 필드에 맞는 것을 찾음
          const dropdownMenus = document.querySelectorAll('.ant-dropdown-menu')
          const targetMenu = dropdownMenus[dropdownMenus.length - 1] // 가장 최근에 열린 드롭다운

          if (targetMenu) {
            const selectedItem = targetMenu.querySelector(`.ant-dropdown-menu-item-selected`)
            if (selectedItem) {
              selectedItem.scrollIntoView({
                block: 'center',
              })
            }
          }
        }, 100)
      }
    }
  }

  return (
    <div className={styles.workHoursPanel}>
      <div className={styles.workHoursPanelHeader}>
        <div className={styles.workHoursPanelHeaderTitle}>
          <span>{t('settings.title.workHours')}</span>
        </div>
      </div>
      <div className={styles.workHoursPanelContent}>
        {workHours.map((workHour, index) => (
          <div className={styles.workHoursPanelContentItem} key={index}>
            <div className={styles.dayCheckbox}>
              <input
                type="checkbox"
                checked={workHour.enabled}
                onChange={() => handleDayToggle(index)}
                className={styles.checkbox}
              />
              <span className={styles.dayLabel}>{dayNames[index]}</span>
            </div>
            <div className={styles.timeSelector}>
              <Dropdown
                menu={{
                  items: createTimeDropdownItems(index, 'start'),
                  selectedKeys: getSelectedKey(index, 'start')
                    ? [getSelectedKey(index, 'start')!.toString()]
                    : [],
                }}
                trigger={['click']}
                placement="bottomLeft"
                overlayClassName={classNames(styles.dropdown, 'contextMenu')}
                disabled={!workHour.enabled}
                destroyOnHidden={true}
                onOpenChange={(open) => handleDropdownOpen(index, 'start', open)}
              >
                <button className={styles.timeButton} disabled={!workHour.enabled}>
                  {formatTime(workHour.start)}
                  <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
                </button>
              </Dropdown>
              <span className={styles.timeSeparator}>
                {t('settings.calendarTabs.timeSeparator')}
              </span>
              <Dropdown
                menu={{
                  items: createTimeDropdownItems(index, 'end'),
                  selectedKeys: getSelectedKey(index, 'end')
                    ? [getSelectedKey(index, 'end')!.toString()]
                    : [],
                }}
                trigger={['click']}
                placement="bottomLeft"
                overlayClassName={classNames(styles.dropdown, 'contextMenu')}
                disabled={!workHour.enabled}
                destroyOnHidden={true}
                onOpenChange={(open) => handleDropdownOpen(index, 'end', open)}
              >
                <button className={styles.timeButton} disabled={!workHour.enabled}>
                  {formatTime(workHour.end)}
                  <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
                </button>
              </Dropdown>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default WorkHoursPanel
